import { z } from "zod";
import { Citation } from "./citation";

const Coordinate = z.tuple([z.number(), z.number()]);
export const WorkpaperData = z.object({
  data: z.record(
    z.string(),
    z.array(
      z.object({
        value: z.string(),
        citations: z.array(Citation).nullable().optional(),
        sourceDocument: z
          .object({
            id: z.string(),
            name: z.string(),
            boundingBox: z
              .tuple([Coordinate, Coordinate, Coordinate, Coordinate])
              .nullable(),
            sourceElementId: z.string().nullable(),
            pageNumber: z.number().nullable(),
            pageWidth: z.number().nullable(),
            pageHeight: z.number().nullable(),
          })
          .nullable()
          .optional(),
        testResult: z.any().nullable(),
        sourceRowId: z.string(),
        attributeId: z.string().nullable(),
        supportColumnId: z.string().nullable(),
        columnVersion: z.number().default(0),
        lockedBy: z.string().nullable(),
        runId: z.string().nullable().optional(),
      }),
    ),
  ),
  attributes: z.array(
    z.object({
      id: z.string(),
      attribute: z.object({
        id: z.string(),
        name: z.string(),
        instruction: z.string().nullable(),
        type: z.string(),
      }),
      attributeOrder: z.number(),
      procedureRequiredDocumentId: z.string().nullable(),
    }),
  ),
});

/**
 * Slim work-paper-data shape (workpaper-lazy-load follow-up).
 *
 * Per Friday cross-repo contract B (option ii): the BE unconditionally
 * strips `citations` from `GET /matching/match-session/{id}/work-paper-data`
 * responses; the per-cell citation list is lazy-fetched via
 * `useCellCitationDetail` on click. The cell-value schema below makes
 * `citations` optional (defaulting to `[]`) so the slim response parses
 * cleanly without any other type widening on the cell-value entry.
 *
 * The existing `WorkpaperData` export is retained for any consumer that
 * still types against the legacy heavy shape — the slim payload is a
 * structural subset (citations missing or empty), so heavy-typed consumers
 * compile against the slim runtime value too.
 */
export const workpaperDataSlimSchema = z.object({
  data: z.record(
    z.string(),
    z.array(
      z.object({
        value: z.string(),
        citations: z.array(Citation).nullable().optional(),
        sourceDocument: z
          .object({
            id: z.string(),
            name: z.string(),
            boundingBox: z
              .tuple([Coordinate, Coordinate, Coordinate, Coordinate])
              .nullable(),
            sourceElementId: z.string().nullable(),
            pageNumber: z.number().nullable(),
            pageWidth: z.number().nullable(),
            pageHeight: z.number().nullable(),
          })
          .nullable()
          .optional(),
        testResult: z.any().nullable(),
        sourceRowId: z.string(),
        attributeId: z.string().nullable(),
        supportColumnId: z.string().nullable(),
        columnVersion: z.number().default(0),
        lockedBy: z.string().nullable(),
        runId: z.string().nullable().optional(),
      }),
    ),
  ),
  attributes: z.array(
    z.object({
      id: z.string(),
      attribute: z.object({
        id: z.string(),
        name: z.string(),
        instruction: z.string().nullable(),
        type: z.string(),
      }),
      attributeOrder: z.number(),
      procedureRequiredDocumentId: z.string().nullable(),
    }),
  ),
});

export type WorkpaperDataSlim = z.infer<typeof workpaperDataSlimSchema>;

/**
 * Response shape for the per-cell citation lazy-fetch endpoint
 * `GET /controls/v2/procedure-session/{ps_id}/source-row/{sr_id}/support-cell/{support_column_id}/citations`.
 *
 * The BE response is a thin envelope around the existing `Citation` model.
 */
export const cellCitationDetailSchema = z.object({
  citations: z.array(Citation).default([]),
});

export type CellCitationDetail = z.infer<typeof cellCitationDetailSchema>;

export const SessionSourceRowData = z.object({
  id: z.string(),
  session_id: z.string(),
  attribute_id: z.string().nullable(),
  support_column_id: z.string().nullable(),
  source_row_id: z.string(),
  data: z.string(),
  bounding_box: z
    .tuple([Coordinate, Coordinate, Coordinate, Coordinate])
    .nullable(),
  source_document_id: z.string().nullable(),
  source_element: z.string().nullable(),
  page_number: z.number().nullable(),
  page_width: z.number().nullable(),
  page_height: z.number().nullable(),
  locked_by: z.string().nullable(),
  column_version: z.number().default(0),
  user_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const SessionSourceRowDataWithCitations = SessionSourceRowData.extend({
  citations: z.array(Citation),
});
